import React from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  Skeleton,
  useTheme,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { Add as AddIcon, FlagOutlined } from "@mui/icons-material";
import { Goal, GoalLinkedAccount } from "../../lib/types";
import GoalCard from "./GoalCard";
import GoalTimeline from "./GoalTimeline";

interface GoalListProps {
  goals: Goal[];
  accounts?: GoalLinkedAccount[];
  loading?: boolean;
  accountsLoading?: boolean;
  categoryOptions?: string[];
  selectedCategories?: string[];
  onSelectedCategoriesChange?: (next: string[]) => void;
  onEdit: (goal: Goal) => void;
  onLinkAccounts: (goal: Goal) => void;
  onAddNew: () => void;
}

const GoalList: React.FC<GoalListProps> = ({
  goals,
  accounts = [],
  loading = false,
  accountsLoading = false,
  categoryOptions = [],
  selectedCategories = [],
  onSelectedCategoriesChange,
  onEdit,
  onLinkAccounts,
  onAddNew,
}) => {
  const theme = useTheme();

  // Sort goals: active first, then paused, then completed
  const sortedGoals = [...goals].sort((a, b) => {
    const statusOrder = { active: 0, paused: 1, completed: 2 };
    return statusOrder[a.status] - statusOrder[b.status];
  });

  // Calculate summary stats
  const activeGoals = goals.filter((g) => g.status === "active");
  const totalTargetAmount = activeGoals.reduce(
    (sum, g) => sum + g.targetAmount,
    0
  );
  const totalCurrentAmount = activeGoals.reduce(
    (sum, g) => sum + g.currentAmount,
    0
  );
  const overallProgress =
    totalTargetAmount > 0 ? (totalCurrentAmount / totalTargetAmount) * 100 : 0;

  // Show empty state only when not loading and no goals
  if (!loading && goals.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          px: 2,
          textAlign: "center",
        }}
      >
        <FlagOutlined
          sx={{
            fontSize: 80,
            color: theme.palette.grey[400],
            mb: 2,
          }}
        />
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          No Goals Yet
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 400 }}
        >
          Start setting financial goals to track your progress toward saving for
          things that matter to you.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddNew}
          size="large"
        >
          Create Your First Goal
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Stats - Header always visible, values show skeleton when loading */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          mb: 3,
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            Active Goals
          </Typography>
          {loading ? (
            <Skeleton width={60} height={42} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {activeGoals.length}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total Target
          </Typography>
          {loading ? (
            <Skeleton width={120} height={42} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              ${totalTargetAmount.toLocaleString()}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Current Progress
          </Typography>
          {loading ? (
            <Skeleton width={120} height={42} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              ${totalCurrentAmount.toLocaleString()}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Overall Progress
          </Typography>
          {loading ? (
            <Skeleton width={80} height={42} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {overallProgress.toFixed(1)}%
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Autocomplete
          multiple
          freeSolo
          options={categoryOptions}
          value={selectedCategories}
          onChange={(_, value) =>
            onSelectedCategoriesChange?.(value.map(String))
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={`${option}-${index}`}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter Categories"
              placeholder="Type to search or add, press Enter"
            />
          )}
        />
      </Box>

      {/* Timeline between summary and grid */}
      <GoalTimeline goals={goals} loading={loading} />

      {/* Goals Grid - Show skeleton cards when loading */}
      <Grid container spacing={3}>
        {loading
          ? Array.from({ length: 6 }).map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={`skeleton-${index}`}>
                <Box
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={48} height={48} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton height={20} width="70%" />
                      <Skeleton height={16} width="50%" />
                    </Box>
                  </Box>
                  <Skeleton variant="rectangular" height={10} sx={{ mb: 2 }} />
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Skeleton height={28} width={80} />
                    <Skeleton height={28} width={80} />
                  </Box>
                  <Skeleton height={18} width="60%" sx={{ mb: 1 }} />
                  <Skeleton height={18} width="40%" />
                </Box>
              </Grid>
            ))
          : sortedGoals.map((goal) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={goal.id}>
                <GoalCard
                  goal={goal}
                  accounts={accounts}
                  accountsLoading={accountsLoading}
                  onEdit={onEdit}
                  onLinkAccounts={onLinkAccounts}
                />
              </Grid>
            ))}
      </Grid>
    </Box>
  );
};

export default GoalList;
